import { NavigationPathsEnum } from "@utilities/enums";
import { useNavigation, useUser } from "@utilities/hooks";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useController = () => {
    const { navigateTo } = useNavigation();
    const { onCreateUserPassword, createPasswordError, onVerifyEmail, verifyEmailError, emailVerifyisSuccess } = useUser();
    const [searchParams] = useSearchParams();
    
    const [email, setEmail] = useState<string>("");
    const [otpCode, setOtpCode] = useState<string>("");
    const [token, setToken] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isVerifyingEmail, setIsVerifyingEmail] = useState<boolean>(true);
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>("");

    useEffect(() => {
        // Récupérer les paramètres de l'URL
        const urlEmail = searchParams.get("email");
        const urlOtp = searchParams.get("otp");
        const urlToken = searchParams.get("token");
        
        if (urlEmail) setEmail(urlEmail);
        if (urlOtp) setOtpCode(urlOtp);
        if (urlToken) setToken(urlToken);
        
        // Si les paramètres requis ne sont pas présents, rediriger vers login
        if (!urlEmail || !urlOtp || !urlToken) {
            navigateTo(NavigationPathsEnum.LOGIN);
            return;
        }

        // Vérifier automatiquement l'email avec l'OTP
        const verifyEmailAutomatically = async () => {
            setIsVerifyingEmail(true);
            await onVerifyEmail(urlEmail, urlOtp);
            // La vérification du résultat se fait dans l'useEffect suivant
        };

        verifyEmailAutomatically();
    }, [searchParams, navigateTo, onVerifyEmail]);

    // Surveiller les changements d'état de la vérification email
    useEffect(() => {
        if (emailVerifyisSuccess) {
            setIsEmailVerified(true);
            setIsVerifyingEmail(false);
        } else if (verifyEmailError) {
            setIsEmailVerified(false);
            setIsVerifyingEmail(false);
        }
    }, [emailVerifyisSuccess, verifyEmailError]);

    const validateForm = useCallback(() => {
        if (!password) {
            setValidationError("Le mot de passe est requis");
            return false;
        }
        if (password.length < 8) {
            setValidationError("Le mot de passe doit contenir au moins 8 caractères");
            return false;
        }
        if (password !== confirmPassword) {
            setValidationError("Les mots de passe ne correspondent pas");
            return false;
        }
        setValidationError("");
        return true;
    }, [password, confirmPassword]);

    const onResetPassword = useCallback(async () => {
        if (!validateForm()) return;
        
        if (token && password) {
            setIsLoading(true);
            try {
                await onCreateUserPassword(token, password);
                setIsSuccess(true);
                
                // Rediriger vers login après 2 secondes
                setTimeout(() => {
                    navigateTo(NavigationPathsEnum.LOGIN);
                }, 2000);
            } catch (error) {
                // L'erreur est gérée par le hook useUser
            } finally {
                setIsLoading(false);
            }
        }
    }, [token, password, validateForm, onCreateUserPassword, navigateTo]);

    return {
        email,
        password,
        confirmPassword,
        isLoading,
        isSuccess,
        isVerifyingEmail,
        isEmailVerified,
        validationError,
        createPasswordError,
        verifyEmailError,
        emailVerifyisSuccess,
        setPassword,
        setConfirmPassword,
        onResetPassword,
    };
};
